import { useState, useCallback, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, Waves, Target } from 'lucide-react';

interface SwimmingLessonsGameProps {
  onComplete: (score: number, stars: number) => void;
  onBack: () => void;
  speciesId: string;
  otterName: string;
}

interface BeatNote {
  id: string;
  targetTime: number;
  hit: boolean | null;
  position: number; // 0-100, percentage from top
}

const SONG_DURATION = 30000; // 30 seconds
const NOTE_FALL_DURATION = 2000; // 2 seconds to fall
const HIT_WINDOW = 200; // ms tolerance
const NOTES_PER_PATTERN = 4;

function generateNotes(): BeatNote[] {
  const notes: BeatNote[] = [];
  const patterns = [
    [0, 500, 1000, 1500],
    [0, 250, 500, 1000],
    [0, 300, 600, 1200],
    [0, 400, 800, 1000],
  ];

  let time = 2000;
  let id = 0;

  while (time < SONG_DURATION - 2000) {
    const pattern = patterns[Math.floor(Math.random() * patterns.length)];
    pattern.forEach((offset, i) => {
      notes.push({
        id: `note-${id++}`,
        targetTime: time + offset,
        hit: null,
        position: -10, // Start above screen
      });
    });
    time += 2500 + Math.random() * 1000;
  }

  return notes;
}

export function SwimmingLessonsGame({ onComplete, onBack, speciesId, otterName }: SwimmingLessonsGameProps) {
  const [notes, setNotes] = useState<BeatNote[]>([]);
  const [score, setScore] = useState(0);
  const [combo, setCombo] = useState(0);
  const [maxCombo, setMaxCombo] = useState(0);
  const [startTime, setStartTime] = useState<number | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [lastHitFeedback, setLastHitFeedback] = useState<'perfect' | 'good' | 'miss' | null>(null);
  const gameRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number | null>(null);

  // Start game
  const startGame = useCallback(() => {
    setNotes(generateNotes());
    setScore(0);
    setCombo(0);
    setMaxCombo(0);
    setStartTime(Date.now());
    setIsPlaying(true);
    setIsComplete(false);
  }, []);

  // Update note positions
  useEffect(() => {
    if (!isPlaying || !startTime) return;

    const update = () => {
      const now = Date.now();
      const elapsed = now - startTime;

      // Check for game end
      if (elapsed >= SONG_DURATION) {
        setIsPlaying(false);
        setIsComplete(true);
        const stars = maxCombo >= 15 ? 3 : maxCombo >= 8 ? 2 : maxCombo >= 3 ? 1 : 0;
        setTimeout(() => onComplete(score, stars), 1500);
        return;
      }

      // Update note positions
      setNotes(prev => prev.map(note => {
        if (note.hit !== null) return note; // Already hit/missed

        const timeDiff = elapsed - note.targetTime + NOTE_FALL_DURATION;
        const position = (timeDiff / NOTE_FALL_DURATION) * 100;

        // Auto-miss if past hit zone
        if (position > 95) {
          setCombo(0);
          return { ...note, hit: false, position };
        }

        return { ...note, position };
      }));

      animationRef.current = requestAnimationFrame(update);
    };

    animationRef.current = requestAnimationFrame(update);
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [isPlaying, startTime, score, maxCombo, onComplete]);

  // Handle tap/click
  const handleTap = useCallback(() => {
    if (!isPlaying || !startTime) return;

    const now = Date.now();
    const elapsed = now - startTime;

    // Find the closest unhit note in the hit zone
    let closestNote: BeatNote | null = null;
    let closestDiff = Infinity;

    notes.forEach(note => {
      if (note.hit !== null) return;

      const diff = Math.abs(elapsed - note.targetTime);
      if (diff < closestDiff && diff < HIT_WINDOW * 2) {
        closestDiff = diff;
        closestNote = note;
      }
    });

    if (closestNote) {
      const foundNote = closestNote as BeatNote;
      const diff = Math.abs(elapsed - foundNote.targetTime);
      let points = 0;
      let feedback: 'perfect' | 'good' | 'miss' = 'miss';

      if (diff < HIT_WINDOW / 2) {
        // Perfect hit
        points = 100;
        feedback = 'perfect';
        setCombo(prev => {
          const newCombo = prev + 1;
          setMaxCombo(max => Math.max(max, newCombo));
          return newCombo;
        });
      } else if (diff < HIT_WINDOW) {
        // Good hit
        points = 50;
        feedback = 'good';
        setCombo(prev => {
          const newCombo = prev + 1;
          setMaxCombo(max => Math.max(max, newCombo));
          return newCombo;
        });
      } else {
        // Miss
        setCombo(0);
      }

      // Apply combo bonus
      const comboBonus = 1 + Math.floor(combo / 5) * 0.1;
      points = Math.floor(points * comboBonus);

      setScore(prev => prev + points);
      setLastHitFeedback(feedback);
      setTimeout(() => setLastHitFeedback(null), 300);

      setNotes(prev => prev.map(n =>
        n.id === foundNote.id ? { ...n, hit: feedback !== 'miss' } : n
      ));
    } else {
      // Missed tap
      setCombo(0);
      setLastHitFeedback('miss');
      setTimeout(() => setLastHitFeedback(null), 300);
    }
  }, [isPlaying, startTime, notes, combo]);

  const progress = startTime ? Math.min(100, ((Date.now() - startTime) / SONG_DURATION) * 100) : 0;

  return (
    <div className="fixed inset-0 bg-gradient-to-b from-blue-900 to-blue-950 flex flex-col">
      {/* Header */}
      <div className="p-4 bg-black/30 z-10">
        <div className="flex items-center justify-between mb-3">
          <button onClick={onBack} className="text-white/60 text-sm">
            ← Back
          </button>
          <h2 className="text-white font-bold">Swimming Lessons</h2>
          <div className="text-white font-bold">{score}</div>
        </div>

        {/* Progress bar */}
        {isPlaying && (
          <div className="flex items-center gap-2">
            <Waves className="w-4 h-4 text-blue-400" />
            <div className="flex-1 h-2 bg-black/30 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-blue-500"
                animate={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}

        {/* Combo display */}
        {isPlaying && combo > 0 && (
          <motion.div
            initial={{ scale: 0.5 }}
            animate={{ scale: 1 }}
            className="text-center mt-2"
          >
            <span className="text-yellow-400 font-bold">
              {combo}x Combo!
            </span>
          </motion.div>
        )}
      </div>

      {/* Game area */}
      <div
        ref={gameRef}
        className="flex-1 relative overflow-hidden touch-none"
        onTouchStart={handleTap}
        onMouseDown={handleTap}
      >
        {/* Water wave background effect */}
        <div className="absolute inset-0 opacity-20">
          {[...Array(5)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-full h-8 bg-blue-400"
              style={{ top: `${20 + i * 20}%` }}
              animate={{
                x: ['-10%', '10%', '-10%'],
              }}
              transition={{
                duration: 3 + i * 0.5,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            />
          ))}
        </div>

        {/* Otter at bottom */}
        <motion.div
          className="absolute bottom-20 left-1/2 -translate-x-1/2 text-6xl"
          animate={{
            y: isPlaying ? [0, -5, 0] : 0,
            rotate: lastHitFeedback === 'perfect' ? [0, -10, 10, 0] : 0,
          }}
          transition={{
            y: { duration: 1, repeat: Infinity },
            rotate: { duration: 0.3 },
          }}
        >
          🦦
        </motion.div>

        {/* Hit zone indicator */}
        <div className="absolute bottom-24 left-1/2 -translate-x-1/2 w-20 h-3 rounded-full bg-white/20 border-2 border-white/40" />

        {/* Falling notes */}
        {notes.map(note => {
          if (note.position < -10 || note.position > 100) return null;

          return (
            <motion.div
              key={note.id}
              className={`
                absolute left-1/2 -translate-x-1/2 w-16 h-4 rounded-full
                ${note.hit === true
                  ? 'bg-green-500'
                  : note.hit === false
                    ? 'bg-red-500 opacity-50'
                    : 'bg-cyan-400 shadow-lg shadow-cyan-400/50'
                }
              `}
              style={{
                top: `${note.position}%`,
              }}
              animate={{
                scale: note.hit === true ? [1, 1.5, 0] : 1,
                opacity: note.hit === false ? 0 : 1,
              }}
              transition={{ duration: 0.2 }}
            />
          );
        })}

        {/* Hit feedback */}
        <AnimatePresence>
          {lastHitFeedback && (
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.5 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20 }}
              className={`
                absolute bottom-32 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full font-bold
                ${lastHitFeedback === 'perfect'
                  ? 'bg-yellow-500 text-yellow-900'
                  : lastHitFeedback === 'good'
                    ? 'bg-green-500 text-green-900'
                    : 'bg-red-500 text-red-100'
                }
              `}
            >
              {lastHitFeedback === 'perfect' ? 'PERFECT!' : lastHitFeedback === 'good' ? 'Good!' : 'Miss'}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Start screen */}
        {!isPlaying && !isComplete && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/50">
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-blue-800 rounded-2xl p-6 text-center mx-4"
            >
              <h3 className="text-2xl font-bold text-white mb-3">
                Swimming Lessons!
              </h3>
              <p className="text-white/80 mb-4">
                Tap in rhythm to help {otterName} learn to swim!
                <br />
                Hit the notes as they reach the target.
              </p>
              <button
                onClick={startGame}
                className="bg-cyan-500 hover:bg-cyan-400 text-white font-bold py-3 px-8 rounded-full text-lg"
              >
                Start Swimming!
              </button>
            </motion.div>
          </div>
        )}

        {/* Completion screen */}
        <AnimatePresence>
          {isComplete && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="absolute inset-0 flex items-center justify-center bg-black/70"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="bg-blue-700 rounded-2xl p-6 text-center mx-4"
              >
                <h3 className="text-2xl font-bold text-white mb-2">
                  Great Lesson!
                </h3>
                <p className="text-white/80 mb-2">
                  {otterName} is swimming better!
                </p>
                <div className="text-3xl font-bold text-yellow-400 mb-2">
                  {score} points
                </div>
                <p className="text-white/60 text-sm mb-3">
                  Best combo: {maxCombo}x
                </p>
                <div className="flex justify-center gap-1">
                  {[1, 2, 3].map(i => (
                    <Star
                      key={i}
                      className={`w-8 h-8 ${
                        (maxCombo >= 15 && i <= 3) || (maxCombo >= 8 && i <= 2) || (maxCombo >= 3 && i <= 1)
                          ? 'text-yellow-400 fill-yellow-400'
                          : 'text-gray-400'
                      }`}
                    />
                  ))}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Tap instruction */}
      {isPlaying && (
        <div className="p-4 bg-black/30 text-center">
          <p className="text-white/50 text-xs">
            TAP anywhere when notes reach the target!
          </p>
        </div>
      )}
    </div>
  );
}
